/**
 * WFP Design System colours for code that cannot use Tailwind classes (ECharts, Leaflet, canvas).
 * Mirrors the `:root` scales of `src/index.css`.
 * @see https://designsystem.wfp.org/docs/foundations/colours
 */
const neutral = {
  50: '#f8f8f8',
  100: '#f1f1f1',
  200: '#e3e3e3',
  300: '#cccccc',
  400: '#aeaeae',
  500: '#8d8d8d',
  600: '#727272',
  700: '#545454',
  800: '#3a3a3a',
  900: '#262626',
  950: '#161616'
} as const;

const neutralAlpha = {
  50: '#00000006',
  100: '#0000000f',
  200: '#0000001c',
  300: '#00000033',
  400: '#00000051',
  500: '#00000072',
  600: '#0000008d',
  700: '#000000ab',
  800: '#000000c5',
  900: '#000000d9',
  950: '#000000e9'
} as const;

const blue = {
  50: '#f4f9fc',
  100: '#e9f2fa',
  200: '#d3e6f5',
  300: '#aed1ec',
  400: '#7bb5df',
  500: '#3f94cc',
  600: '#0078b8',
  700: '#005795',
  800: '#003b6d',
  900: '#00274e',
  950: '#001632'
} as const;

const aqua = {
  50: '#f4f9fc',
  100: '#e5f3f8',
  200: '#cbe8f3',
  300: '#9ed4e9',
  400: '#65b9d8',
  500: '#2399bd',
  600: '#007c9e',
  700: '#005b78',
  800: '#003f55',
  900: '#002a3b',
  950: '#001925'
} as const;

const green = {
  50: '#f5faf6',
  100: '#e8f4eb',
  200: '#d2e9d7',
  300: '#abd6b5',
  400: '#7abe8a',
  500: '#38a05b',
  600: '#008442',
  700: '#006147',
  800: '#034332',
  900: '#022d21',
  950: '#011b12'
} as const;

const ivory = {
  50: '#fbf9ee',
  100: '#f6f0d9',
  200: '#ede3b7',
  300: '#d7cc9d',
  400: '#baaf81',
  500: '#988e60',
  600: '#7d7246',
  700: '#5f5429',
  800: '#443a0c',
  900: '#302600',
  950: '#1f1600'
} as const;

const brown = {
  50: '#fdf9f7',
  100: '#faeee9',
  200: '#f5ded3',
  300: '#edc3af',
  400: '#dfa082',
  500: '#c97952',
  600: '#af5b2e',
  700: '#8b3e10',
  800: '#652801',
  900: '#461900',
  950: '#2d0c00'
} as const;

const orange = {
  50: '#fff8f5',
  100: '#ffeee8',
  200: '#ffdcd0',
  300: '#ffbda8',
  400: '#ff9173',
  500: '#f65834',
  600: '#d23b16',
  700: '#a22300',
  800: '#751300',
  900: '#520900',
  950: '#340400'
} as const;

const red = {
  50: '#fff7f6',
  100: '#ffedeb',
  200: '#ffdad6',
  300: '#ffbbb5',
  400: '#ff8f89',
  500: '#ff4b4f',
  600: '#e4052c',
  700: '#ad000c',
  800: '#7c0001',
  900: '#560000',
  950: '#370000'
} as const;

const purple = {
  50: '#fdf8fb',
  100: '#faeef7',
  200: '#f6dcee',
  300: '#ecbfe0',
  400: '#de98ce',
  500: '#cc6bb9',
  600: '#ba40a5',
  700: '#a50067',
  800: '#760143',
  900: '#52002d',
  950: '#34001b'
} as const;

const warning = {
  50: '#fff8e7',
  100: '#fff0cf',
  200: '#fee09c',
  300: '#fcc524',
  400: '#dea600',
  500: '#ba8300',
  600: '#9d6700',
  700: '#7d4800',
  800: '#602c00',
  900: '#4a1500',
  950: '#370100'
} as const;

export const palette = {
  white: '#fff',
  black: '#000',
  neutral,
  neutralAlpha,
  blue,
  aqua,
  green,
  ivory,
  brown,
  orange,
  red,
  purple,
  primary: blue,
  info: blue,
  success: green,
  warning,
  danger: red
} as const;
